import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead } from "@mui/material";
import { Dispatch, ReactNode, SetStateAction } from "react";
import {
    usePortfolioSelection,
    usePortfolioProfile,
    useOrders,
} from "../../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { useAppContext } from "../../../../shared/contexts/app-context/components/AppContext";
import { PortfolioRecord } from "../../../../shared/types/PortfolioRecord";
import { ApplicationTooltip } from "../../../positions-and-orders/views";
import {
    POSITIONS_TABLE_ROW_PADDING_Y,
    TABLE_HEADER_ROW_FONT_SIZE
} from "../../../shared/constants/table.constants";
import { TableBodyRow, TableHeaderRow } from "../../../shared/views";
import { PortfolioBalancerItemModel } from "./PortfolioBalancerItemModel";

type BalanceField = keyof Pick<PortfolioRecord, "currentDateBalance" | "expirationDateBalance">;

interface PortfolioBalancerMeasurementConfig {
    title: string;
    shortBalanceField: BalanceField;
    longBalanceField: BalanceField;
    shortBalanceHeader: string;
    longBalanceHeader: string;
    shortDeltaHeader: string;
    longDeltaHeader: string;
    shortBalanceTooltip: string;
    longBalanceTooltip: string;
    shortDeltaTooltip: string;
    longDeltaTooltip: string;
}

interface PortfolioBalancerProps {
    measurementConfig: PortfolioBalancerMeasurementConfig;
    selectedRow: PortfolioBalancerItemModel | null;
    setSelectedRow: Dispatch<SetStateAction<PortfolioBalancerItemModel | null>>;
}

export const currentShortExpirationLongConfig: PortfolioBalancerMeasurementConfig = {
    title: "🡄 Current balance / Expiration balance 🡆",
    shortBalanceField: "currentDateBalance",
    longBalanceField: "expirationDateBalance",
    shortBalanceHeader: "Cur short",
    longBalanceHeader: "Exp long",
    shortDeltaHeader: "Cur delta",
    longDeltaHeader: "Exp delta",
    shortBalanceTooltip: "Portfolio current date balance on the 'Short price' point",
    longBalanceTooltip: "Portfolio expiration date balance on the 'Long price' point",
    shortDeltaTooltip: "Trading result between the current price and the current date price on the 'Short price' point",
    longDeltaTooltip: "Trading result between the current price and the expiration date price on the 'Long price' point",
};

export const expirationShortCurrentLongConfig: PortfolioBalancerMeasurementConfig = {
    title: "🡄 Expiration balance / Current balance 🡆",
    shortBalanceField: "expirationDateBalance",
    longBalanceField: "currentDateBalance",
    shortBalanceHeader: "Exp short",
    longBalanceHeader: "Cur long",
    shortDeltaHeader: "Exp delta",
    longDeltaHeader: "Cur delta",
    shortBalanceTooltip: "Portfolio expiration date balance on the 'Short price' point",
    longBalanceTooltip: "Portfolio current date balance on the 'Long price' point",
    shortDeltaTooltip: "Trading result between the current price and the expiration date price on the 'Short price' point",
    longDeltaTooltip: "Trading result between the current price and the current date price on the 'Long price' point",
};

export function PortfolioBalancer({ measurementConfig, selectedRow, setSelectedRow }: PortfolioBalancerProps) {
    const {
        selectedPortfolio,
    } = usePortfolioSelection();
    const {
        portfolioProfile,
    } = usePortfolioProfile();
    const {
        marketDataDto,
    } = useOrders();
    const {
        setShowBalancerPriceLines,
        setBalancerPriceLineDelta,
    } = useAppContext();
    
    const deltaPriceStep = 500;
    const calculationCount = 12;
    
    const getBalance = (multiplier: number, direction: -1 | 1, balanceField: BalanceField): number => {
        if (!marketDataDto || !marketDataDto.UnderlyingPrice || !selectedPortfolio || !selectedPortfolio.portfolioSettings)
            return 0;
        
        const priceShift = deltaPriceStep * multiplier * direction;
        const targetPrice = selectedPortfolio.portfolioSettings.useCustomPrice
            ? selectedPortfolio.portfolioSettings.customPrice + priceShift
            : marketDataDto.UnderlyingPrice + priceShift;

        const records = portfolioProfile.data;
        
        for (let i = 0; i < records.length - 1; i++) {
            if (targetPrice >= records[i].price && targetPrice < records[i + 1].price) {
                const deltaToLowerPrice = targetPrice - records[i].price;
                const deltaToHigherPrice = records[i + 1].price - targetPrice;
                
                return deltaToLowerPrice <= deltaToHigherPrice
                    ? records[i][balanceField]
                    : records[i + 1][balanceField];
            }
        }
        
        return 0;
    }
    
    const getCalculatedModels = (): PortfolioBalancerItemModel[] => {
        if (!marketDataDto || !marketDataDto.UnderlyingPrice || !selectedPortfolio || !selectedPortfolio.portfolioSettings)
            return [];
        
        const result: PortfolioBalancerItemModel[] = [];

        const targetPrice = selectedPortfolio.portfolioSettings.useCustomPrice
            ? selectedPortfolio.portfolioSettings.customPrice
            : marketDataDto.UnderlyingPrice;
        
        for (let multiplier = 0; multiplier <= calculationCount; multiplier++) {
            const shortBalance = getBalance(multiplier, -1, measurementConfig.shortBalanceField);
            const longBalance = getBalance(multiplier, 1, measurementConfig.longBalanceField);
            const shortDelta = multiplier !== 0
                ? shortBalance - result[0].shortBalance
                : 0;
            const longDelta = multiplier !== 0
                ? longBalance - result[0].longBalance
                : 0;
            const disbalance = shortDelta - longDelta;
            const balancingOrderSize = multiplier !== 0
                ? (disbalance / 2) / (deltaPriceStep * multiplier)
                : 0;
            
            const model: PortfolioBalancerItemModel = {
                multiplier: multiplier,
                deltaPrice: deltaPriceStep * multiplier,
                shortPrice: targetPrice - deltaPriceStep * multiplier,
                longPrice: targetPrice + deltaPriceStep * multiplier,
                shortBalance: shortBalance,
                longBalance: longBalance,
                shortDelta: shortDelta,
                longDelta: longDelta,
                balancedShortDelta: shortDelta - disbalance / 2,
                disbalance: disbalance,
                balancingOrderSize: balancingOrderSize,
            };

            result.push(model);
        }
        
        return result;
    }
    
    const models = getCalculatedModels();

    const handleSelectedRow = (row: PortfolioBalancerItemModel): void => {
        setShowBalancerPriceLines(row.multiplier !== 0);
        setBalancerPriceLineDelta(row.multiplier !== 0
            ? row.deltaPrice
            : null);

        setSelectedRow(row);
    };
    
    const renderHeaderCell = (title: ReactNode, caption: ReactNode) => (
        <ApplicationTooltip
            title={title}
            disableInteractive
            offset={[0, -7]}
        >
            <TableCell>{caption}</TableCell>
        </ApplicationTooltip>
    );
    
    return (
        <Paper
            sx={{
                flex: '1',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 0,
            }}
        >
            <Box
                sx={{
                    fontSize: TABLE_HEADER_ROW_FONT_SIZE,
                    fontWeight: "700",
                    paddingX: '4px',
                    paddingY: '3px',
                    backgroundColor: 'rgb(245,245,245)',
                    borderBottom: '1px solid rgb(224,224,224)',
                }}
            >
                {measurementConfig.title}
            </Box>
            <TableContainer
                sx={{
                    flex: '1',
                    padding: '0px',
                    margin: '0px',
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    aria-label={measurementConfig.title}
                    sx={{padding: '0px', margin: '0px', verticalAlign: 'text-top'}}
                >
                    <TableHead>
                        <TableHeaderRow>
                            {renderHeaderCell(
                                "The price step that measures shift from the current price",
                                "Price ±"
                            )}
                            {renderHeaderCell(
                                "The underlying price to the lower direction from the current one according to the step value",
                                "Short price"
                            )}
                            {renderHeaderCell(
                                "The underlying price to the higher direction from the current one according to the step value",
                                "Long price"
                            )}
                            {renderHeaderCell(
                                measurementConfig.shortBalanceTooltip,
                                measurementConfig.shortBalanceHeader
                            )}
                            {renderHeaderCell(
                                measurementConfig.longBalanceTooltip,
                                measurementConfig.longBalanceHeader
                            )}
                            {renderHeaderCell(
                                measurementConfig.shortDeltaTooltip,
                                measurementConfig.shortDeltaHeader
                            )}
                            {renderHeaderCell(
                                measurementConfig.longDeltaTooltip,
                                measurementConfig.longDeltaHeader
                            )}
                            {renderHeaderCell(
                                "Difference in trading result",
                                "Disbalance"
                            )}
                            {renderHeaderCell(
                                "Necessary order size for balancing portfolio trading result",
                                "Bal order"
                            )}
                            {renderHeaderCell(
                                "Balanced trading result between the current price and the price on the 'Short price' or 'Long price' point after balancing portfolio",
                                "Short bal"
                            )}
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        {models.map((row) => {
                            const currentRowCellStyle = row.multiplier === 0
                                ? { fontWeight: 700 }
                                : undefined;
                            const boldCellStyle = { fontWeight: 700 };
                            const isSelectedRow = selectedRow === null
                                ? row.multiplier === 0
                                : selectedRow.multiplier === row.multiplier &&
                                    selectedRow.deltaPrice === row.deltaPrice;

                            return (
                                <TableBodyRow
                                    key={row.deltaPrice}
                                    onClick={() => handleSelectedRow(row)}
                                    paddingY={POSITIONS_TABLE_ROW_PADDING_Y}
                                    style={{
                                        ...(isSelectedRow
                                            ? {
                                                backgroundColor: 'rgba(255,224,122,0.5)',
                                            }
                                            : {}),
                                    }}
                                >
                                    <TableCell align="right" style={boldCellStyle}>
                                        {row.multiplier === 0
                                            ? "Current"
                                            : row.deltaPrice.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={currentRowCellStyle}>
                                        {row.multiplier === 0
                                            ? ""
                                            : row.shortPrice.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={currentRowCellStyle}>
                                        {row.multiplier === 0
                                            ? ""
                                            : row.longPrice.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={currentRowCellStyle}>
                                        {row.shortBalance.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={currentRowCellStyle}>
                                        {row.longBalance.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={boldCellStyle}>
                                        {row.multiplier === 0
                                            ? ""
                                            : row.shortDelta.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={boldCellStyle}>
                                        {row.multiplier === 0
                                            ? ""
                                            : row.longDelta.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={currentRowCellStyle}>
                                        {row.multiplier === 0
                                            ? ""
                                            : row.disbalance.toFixed(0)}
                                    </TableCell>
                                    <TableCell align="right" style={currentRowCellStyle}>
                                        {row.multiplier === 0
                                            ? ""
                                            : row.balancingOrderSize.toFixed(3)}
                                    </TableCell>
                                    <TableCell align="right" style={currentRowCellStyle}>
                                        {row.multiplier === 0
                                            ? ""
                                            : row.balancedShortDelta.toFixed(0)}
                                    </TableCell>
                                </TableBodyRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}
